import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Save } from "lucide-react";
import FileUploader from "./FileUploader";

const empty = {
    slug: "", title: "", tagline: "", cover_image: "", summary: "", description: "",
    outcomes: [], published: true, order: 100,
};

export default function AdminProgrammes() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(null);

    const load = useCallback(
        () => api.get("/programmes?all=true").then((r) => setItems(r.data || [])),
        []
    );
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        try {
            const payload = { ...form, outcomes: form.outcomes || [], order: Number(form.order) || 100 };
            if (form.id) await api.put(`/programmes/${form.id}`, payload);
            else await api.post("/programmes", payload);
            toast.success("Saved");
            setForm(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this programme?")) return;
        await api.delete(`/programmes/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div data-testid="admin-programmes">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-semibold">Programmes</h1>
                    <p className="text-sm text-slate-500 mt-1">Add and manage your programmes.</p>
                </div>
                <button onClick={() => setForm({ ...empty })} data-testid="programme-add-btn" className="px-4 py-2 rounded-md bg-brand-forest text-white text-sm font-semibold hover:bg-brand-forestHover transition-colors duration-200 inline-flex items-center gap-2">
                    <Plus size={14} /> New programme
                </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-5 py-3">Title</th>
                            <th className="text-left px-5 py-3">Slug</th>
                            <th className="text-left px-5 py-3">Order</th>
                            <th className="text-left px-5 py-3">Status</th>
                            <th className="text-right px-5 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.id} className="border-t border-slate-100" data-testid={`row-${p.slug}`}>
                                <td className="px-5 py-3">{p.title}</td>
                                <td className="px-5 py-3 text-slate-500">{p.slug}</td>
                                <td className="px-5 py-3">{p.order}</td>
                                <td className="px-5 py-3">
                                    <span className={`text-xs font-semibold px-2 py-1 rounded ${p.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                                        {p.published ? "Published" : "Draft"}
                                    </span>
                                </td>
                                <td className="px-5 py-3 text-right">
                                    <button onClick={() => setForm({ ...p })} data-testid={`edit-${p.slug}`} className="p-2 hover:bg-slate-100 rounded"><Pencil size={14} /></button>
                                    <button onClick={() => remove(p.id)} data-testid={`delete-${p.slug}`} className="p-2 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {form && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setForm(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="programme-editor">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
                            <h2 className="font-semibold">{form.id ? "Edit programme" : "New programme"}</h2>
                            <button onClick={() => setForm(null)}><X size={18} /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="col-span-2 px-3 py-2 rounded border border-slate-200 text-sm" />
                                <input placeholder="Slug (e.g. education-support)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="px-3 py-2 rounded border border-slate-200 text-sm" />
                                <input type="number" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="px-3 py-2 rounded border border-slate-200 text-sm" />
                                <input placeholder="Tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="col-span-2 px-3 py-2 rounded border border-slate-200 text-sm" />
                                <textarea rows="2" placeholder="Summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="col-span-2 px-3 py-2 rounded border border-slate-200 text-sm resize-none" />
                                <textarea rows="5" placeholder="Full description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="col-span-2 px-3 py-2 rounded border border-slate-200 text-sm resize-none" />
                                <input placeholder="Outcomes (comma-separated)" value={(form.outcomes || []).join(", ")} onChange={(e) => setForm({ ...form, outcomes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="col-span-2 px-3 py-2 rounded border border-slate-200 text-sm" />
                                <div className="col-span-2">
                                    <FileUploader kind="image" current={form.cover_image} label="Cover image" onUploaded={(r) => setForm({ ...form, cover_image: r.url })} />
                                    <input placeholder="Or paste image URL" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className="mt-2 w-full px-3 py-2 rounded border border-slate-200 text-sm" />
                                </div>
                                <label className="col-span-2 inline-flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
                                </label>
                            </div>
                        </div>
                        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
                            <button onClick={() => setForm(null)} className="px-4 py-2 rounded text-sm">Cancel</button>
                            <button onClick={save} data-testid="programme-save" className="px-4 py-2 rounded bg-brand-forest text-white text-sm font-semibold inline-flex items-center gap-2"><Save size={14} /> Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
